// src/app/tools/random-name/page.tsx
'use client';

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Copy, RefreshCw, Check, User, Users, Baby, Crown, Globe, Hash } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { randomNameGeneratorContent } from '../../data/random-name-generator-content';
import Link from 'next/link';

// Sample name data
const nameData = {
    firstNames: {
        male: ['James', 'John', 'Robert', 'Michael', 'William', 'David', 'Richard', 'Joseph', 'Thomas', 'Charles'],
        female: ['Mary', 'Patricia', 'Jennifer', 'Linda', 'Elizabeth', 'Barbara', 'Susan', 'Jessica', 'Sarah', 'Karen'],
        unisex: ['Taylor', 'Jordan', 'Alex', 'Casey', 'Riley', 'Jamie', 'Morgan', 'Avery', 'Quinn', 'Peyton'],
        fantasy: ['Aelar', 'Briar', 'Caelum', 'Dravin', 'Eirian', 'Fenris', 'Gideon', 'Isolde', 'Jareth', 'Kaelith'],
    },
    lastNames: [
        'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez',
        'Elendil', 'Stormrider', 'Blackwood', 'Brightblade', 'Dragonheart', 'Frostmane', 'Goldenshield', 'Ironscale'
    ],
    fantasyTitles: ['the Brave', 'the Wise', 'the Fierce', 'the Mysterious', 'the Cunning', 'the Noble', 'the Shadow', 'the Radiant']
};

type NameType = 'first' | 'last' | 'full' | 'fantasy' | 'username';
type Gender = 'male' | 'female' | 'unisex';

export default function RandomNameGenerator() {
    const [name, setName] = useState('');
    const [nameType, setNameType] = useState<NameType>('full');
    const [gender, setGender] = useState<Gender>('unisex');
    const [includeMiddle, setIncludeMiddle] = useState(false);
    const [includeTitle, setIncludeTitle] = useState(false);
    const [count, setCount] = useState(5);
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();

    const getRandomElement = useCallback(<T,>(array: T[]): T => {
        return array[Math.floor(Math.random() * array.length)];
    }, []);

    const generateName = useCallback(() => {
        let newName = '';

        const getRandomFirstName = () => {
            return getRandomElement(nameData.firstNames[gender] || []);
        };

        const getRandomLastName = () => {
            return getRandomElement(nameData.lastNames);
        };

        const getRandomTitle = () => {
            return getRandomElement(nameData.fantasyTitles);
        };

        switch (nameType) {
            case 'first':
                newName = getRandomFirstName();
                break;
            case 'last':
                newName = getRandomLastName();
                break;
            case 'full':
                newName = `${getRandomFirstName()} ${includeMiddle ? getRandomFirstName() + ' ' : ''}${getRandomLastName()}`;
                break;
            case 'fantasy':
                const firstName = getRandomElement(nameData.firstNames.fantasy);
                const lastName = getRandomElement(nameData.lastNames);
                newName = `${firstName} ${lastName}${includeTitle ? ' ' + getRandomTitle() : ''}`;
                break;
            case 'username':
                const prefix = getRandomElement(['The', 'Mr', 'Ms', 'Dr', 'Lord', 'Lady', 'Sir', 'Captain']);
                const noun = getRandomElement(['Dragon', 'Wolf', 'Eagle', 'Phoenix', 'Tiger', 'Lion', 'Bear', 'Hawk']);
                const number = Math.floor(Math.random() * 1000);
                newName = `${prefix}${noun}${number}`.replace(/\s+/g, '');
                break;
        }

        return newName;
    }, [nameType, gender, includeMiddle, includeTitle]);

    const generateNewName = useCallback(() => {
        setName(generateName());
        setCopied(false);
    }, [generateName]);

    const generateMultipleNames = useCallback(() => {
        const names = [];
        for (let i = 0; i < count; i++) {
            names.push(generateName());
        }
        return names.join('\n');
    }, [count, generateName]);

    const copyToClipboard = useCallback((text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast({
            title: 'Copied!',
            description: 'Name has been copied to clipboard.',
        });
        setTimeout(() => setCopied(false), 2000);
    }, [toast]);

    useEffect(() => {
        generateNewName();
    }, [generateNewName]);

    return (
        <div className="container mx-auto px-4 py-8">
            <ToolPageIntro title="Random Name Generator">
                Generate random names with our free online tool. Create first names, last names, and full names for testing, writing, and creative projects.
            </ToolPageIntro>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Generate Names</CardTitle>
                            <CardDescription>Create random names for any purpose</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="space-y-2">
                                <div className="relative">
                                    <Input
                                        type="text"
                                        value={name}
                                        readOnly
                                        className="text-lg font-mono h-12 pr-24"
                                        placeholder="Your name will appear here"
                                    />
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex space-x-1">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => copyToClipboard(name)}
                                            disabled={!name}
                                            className="h-8 w-8"
                                            aria-label="Copy to clipboard"
                                        >
                                            {copied ? (
                                                <Check className="h-4 w-4" />
                                            ) : (
                                                <Copy className="h-4 w-4" />
                                            )}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            onClick={generateNewName}
                                            className="h-8 w-8"
                                            aria-label="Generate new name"
                                        >
                                            <RefreshCw className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Name Type</Label>
                                    <Select value={nameType} onValueChange={(value) => setNameType(value as NameType)}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select name type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="first">First Name</SelectItem>
                                            <SelectItem value="last">Last Name</SelectItem>
                                            <SelectItem value="full">Full Name</SelectItem>
                                            <SelectItem value="fantasy">Fantasy Name</SelectItem>
                                            <SelectItem value="username">Username</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Gender</Label>
                                    <Select
                                        value={gender}
                                        onValueChange={(value) => setGender(value as Gender)}
                                        disabled={nameType === 'username'}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select gender" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="male">Male</SelectItem>
                                            <SelectItem value="female">Female</SelectItem>
                                            <SelectItem value="unisex">Unisex</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {(nameType === 'full' || nameType === 'fantasy') && (
                                    <div className="space-y-2">
                                        <Label>Options</Label>
                                        <div className="space-y-2">
                                            {nameType === 'full' && (
                                                <div className="flex items-center space-x-2">
                                                    <Switch
                                                        id="include-middle"
                                                        checked={includeMiddle}
                                                        onCheckedChange={setIncludeMiddle}
                                                    />
                                                    <Label htmlFor="include-middle">Include Middle Name</Label>
                                                </div>
                                            )}
                                            {nameType === 'fantasy' && (
                                                <div className="flex items-center space-x-2">
                                                    <Switch
                                                        id="include-title"
                                                        checked={includeTitle}
                                                        onCheckedChange={setIncludeTitle}
                                                    />
                                                    <Label htmlFor="include-title">Include Title</Label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="pt-4">
                                <Button
                                    onClick={generateNewName}
                                    className="w-full md:w-auto"
                                >
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    Generate New Name
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Multiple Names</CardTitle>
                            <CardDescription>Generate multiple names at once</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <div className="flex-1">
                                        <Label htmlFor="count" className="block mb-2">Number of names (1-20)</Label>
                                        <Input
                                            id="count"
                                            type="number"
                                            min="1"
                                            max="20"
                                            value={count}
                                            onChange={(e) => setCount(Math.min(Math.max(1, parseInt(e.target.value) || 1), 20))}
                                            className="w-24"
                                        />
                                    </div>
                                    <Button
                                        onClick={() => copyToClipboard(generateMultipleNames())}
                                        className="mt-6"
                                        variant="outline"
                                    >
                                        <Copy className="mr-2 h-4 w-4" />
                                        Copy All
                                    </Button>
                                </div>
                                <div className="bg-muted p-4 rounded-md">
                                    <pre className="text-sm overflow-auto max-h-60">
                                        {generateMultipleNames()}
                                    </pre>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Name Ideas</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <h3 className="font-semibold">First Names</h3>
                                <div className="flex flex-wrap gap-2">
                                    {nameData.firstNames.male.slice(0, 5).map((name, i) => (
                                        <span key={`male-${i}`} className="bg-muted px-2 py-1 rounded text-sm">{name}</span>
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {nameData.firstNames.female.slice(0, 5).map((name, i) => (
                                        <span key={`female-${i}`} className="bg-muted px-2 py-1 rounded text-sm">{name}</span>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-semibold">Fantasy Names</h3>
                                <div className="grid grid-cols-1 gap-2 text-sm">
                                    {Array(3).fill(0).map((_, i) => {
                                        const firstName = getRandomElement(nameData.firstNames.fantasy);
                                        const lastName = getRandomElement(nameData.lastNames);
                                        const title = getRandomElement(nameData.fantasyTitles);
                                        return (
                                            <div key={`fantasy-${i}`} className="bg-muted p-2 rounded">
                                                {`${firstName} ${lastName} ${title}`}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-semibold">Usernames</h3>
                                <div className="flex flex-wrap gap-2">
                                    {Array(6).fill(0).map((_, i) => {
                                        const prefix = getRandomElement(['The', 'Mr', 'Ms', 'Dr']);
                                        const noun = getRandomElement(['Dragon', 'Wolf', 'Eagle', 'Phoenix']);
                                        const number = Math.floor(Math.random() * 1000);
                                        return (
                                            <span key={`user-${i}`} className="bg-muted px-2 py-1 rounded text-sm">
                                                {`${prefix}${noun}${number}`.replace(/\s+/g, '')}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Name Ideas by Category</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <h3 className="font-semibold flex items-center">
                                    <Baby className="h-4 w-4 mr-2" />
                                    Baby Names
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {['Emma', 'Liam', 'Olivia', 'Noah', 'Ava', 'William', 'Sophia', 'James'].map((name, i) => (
                                        <span key={`baby-${i}`} className="bg-muted px-2 py-1 rounded text-sm">{name}</span>
                                    ))}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {['Emma', 'Liam', 'Olivia', 'Noah', 'Ava', 'William', 'Sophia', 'James'].map((name, i) => (
                                        <span key={`baby-${i}`} className="bg-muted px-2 py-1 rounded text-sm">{name}</span>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-semibold flex items-center">
                                    <Crown className="h-4 w-4 mr-2" />
                                    Fantasy Characters
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {['Aragorn', 'Galadriel', 'Gandalf', 'Daenerys', 'Tyrion', 'Arya', 'Geralt', 'Yennefer'].map((name, i) => (
                                        <span key={`fantasy-${i}`} className="bg-muted px-2 py-1 rounded text-sm">{name}</span>
                                    ))}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <h3 className="font-semibold flex items-center">
                                    <Globe className="h-4 w-4 mr-2" />
                                    International Names
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {['Hiroshi', 'Sofia', 'Mohammed', 'Wei', 'Anastasia', 'Mateo', 'Amina', 'Nikolai'].map((name, i) => (
                                        <span key={`intl-${i}`} className="bg-muted px-2 py-1 rounded text-sm">{name}</span>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Informational Content */}
            <InformationalContent
                title={randomNameGeneratorContent.title}
                description={randomNameGeneratorContent.description}
                content={randomNameGeneratorContent.content}
                faqs={randomNameGeneratorContent.faqs}
                relatedTools={randomNameGeneratorContent.relatedTools}
            />
        </div >
    );
}